// Data for /compare/:slug competitor pages (the two original bespoke pages,
// vs-spreadsheets and vs-flight-circle, remain standalone). Tone rules:
// qualitative and fair, credit competitors where credit is due, no pricing
// claims about other products, and every Resyrv claim maps to a shipped
// capability.

export const COMPETITORS = [
  {
    slug: 'vs-flight-schedule-pro',
    name: 'Flight Schedule Pro',
    shortName: 'FSP',
    title: 'Resyrv vs Flight Schedule Pro | Honest Comparison',
    metaDescription:
      'Flight Schedule Pro is the incumbent for large training operations. Resyrv is the modern alternative for clubs, partnerships, and schools that want scheduling, maintenance, and money in one system. An honest comparison.',
    photo: '/marketing/twin-taxi-pilot.webp',
    photoWidth: 1800,
    photoHeight: 1239,
    h1: 'The incumbent got big.',
    h1Accent: 'You might not need big.',
    heroCopy:
      'Flight Schedule Pro has served flight training for decades, and large academies run deep on it. Resyrv is for the rest of the market: clubs, partnerships, and schools that want modern scheduling, enforced maintenance, and automated money without enterprise weight.',
    intro: [
      'Credit first: Flight Schedule Pro is the name in flight-school scheduling for a reason. University programs and multi-location academies with dedicated administrative staff use its depth - extensive training records, dispatch desk workflows, a large module catalog - and genuinely need it.',
      'The question is whether you are that operation. Most clubs and independent schools are not, and they end up paying for enterprise surface area they never touch while the essentials - who owes what, whether the airplane is airworthy, whether the money reconciles - still live partly in spreadsheets. Resyrv starts from those essentials and keeps the whole operation in one system.',
    ],
    rows: [
      {
        dimension: 'Built for',
        them: 'Large training operations: academies, universities, multi-location schools.',
        resyrv: 'Shared aircraft in all forms: partnerships, clubs, independent schools, rental operators.',
        edge: false,
      },
      {
        dimension: 'Cost sharing & partnership billing',
        them: 'Training-operation billing focus; partnership split rules are not the center of gravity.',
        resyrv: 'Native: split rules, reserve accruals per hour, member statements, FIFO payment allocation.',
        edge: true,
      },
      {
        dimension: 'Maintenance',
        them: 'Tracks inspections and squawks with dispatch workflows.',
        resyrv: 'Tracks and enforces: grounding flags remove an aircraft from the schedule automatically.',
        edge: true,
      },
      {
        dimension: 'Member payments',
        them: 'Payment processing available within its billing modules.',
        resyrv: 'Stripe or Authorize.net per organization, ACH and cards, Cash App Pay, autopay, self-serve statements.',
        edge: false,
      },
      {
        dimension: 'Getting started',
        them: 'Enterprise-style onboarding matched to its depth.',
        resyrv: 'Self-serve: partnerships fly the same afternoon, schools typically live within a week.',
        edge: true,
      },
      {
        dimension: 'Training tools',
        them: 'Deep, mature training and compliance features for structured programs.',
        resyrv: 'Training records, instructor scheduling, and a course store - sized for clubs and independent schools.',
        edge: false,
      },
      {
        dimension: 'Leaving',
        them: 'Data export on their terms.',
        resyrv: 'Logbooks export in ForeFlight format, financials to QuickBooks or Xero, everything else as CSV and PDF.',
        edge: true,
      },
    ],
    chooseThem: [
      'You run a Part 141 academy or university program with dedicated admin and dispatch staff.',
      'You need their specific curriculum, LMS, or enterprise integrations.',
      'Your operation already runs on it smoothly and the cost is not a pain point.',
    ],
    faq: [
      {
        q: 'Can we switch mid-semester?',
        a: 'Groups usually run the two side by side for a short overlap: bring the roster, balances, and aircraft baselines into Resyrv, cut the schedule over on a quiet week, and keep read access to historical records for reference. Most of the migration effort is the data import, which Resyrv helps with.',
      },
      {
        q: 'Is Resyrv deep enough for a real school?',
        a: 'For independent and mid-size schools, yes: instructor availability, student scheduling rules, training records, enforced airworthiness, and full billing are all native. What Resyrv deliberately does not chase is the enterprise module catalog a university program needs - if that is you, FSP earns its place.',
      },
      {
        q: 'What does Resyrv cost by comparison?',
        a: 'We publish flat plans on the pricing page - no per-module math and no sales call required. We will not quote another vendor\'s pricing here; compare your current invoice against the pricing page and the answer is usually quick.',
      },
    ],
  },
  {
    slug: 'vs-coflyt',
    name: 'Coflyt',
    shortName: 'Coflyt',
    title: 'Resyrv vs Coflyt | Honest Comparison',
    metaDescription:
      'Coflyt is a tidy aircraft-tracking app for owners. Resyrv is a full operations platform for partnerships and clubs: real billing, reserves, enforcement, and accounting sync. An honest comparison.',
    photo: '/marketing/twin-dusk.webp',
    photoWidth: 1800,
    photoHeight: 1200,
    h1: 'Tracking your airplane is step one.',
    h1Accent: 'Running the partnership is the job.',
    heroCopy:
      'Coflyt does a genuinely nice job reminding owners about maintenance and organizing simple scheduling. Resyrv picks up where reminders stop: statements, reserves, payments, permissions, and records that hold up as the group grows.',
    intro: [
      'Coflyt earned its following by being simple: tach tracking, inspection reminders, squawk notes, and basic scheduling in a clean app. For a sole owner, or two partners who settle up over text, that may honestly be all you need - and if so, use it happily.',
      'Partnerships stop being that simple the day money gets complicated: reserve funds, a partner who flies triple the hours, statements someone has to produce, a treasurer who would like their evenings back. Those are the jobs a tracking app was never built to do.',
    ],
    rows: [
      {
        dimension: 'Built for',
        them: 'Individual owners and small partnerships tracking an airplane.',
        resyrv: 'Partnerships, clubs, schools, and operators running an operation.',
        edge: false,
      },
      {
        dimension: 'Billing',
        them: 'Cost tracking and simple sharing oriented around visibility.',
        resyrv: 'Automated statements: every flight meters into a charge, split rules apply, month closes itself.',
        edge: true,
      },
      {
        dimension: 'Reserves',
        them: 'Maintenance awareness through reminders and tracking.',
        resyrv: 'Funded reserve accounting: engine, prop, and avionics accrue per hour flown, balances visible.',
        edge: true,
      },
      {
        dimension: 'Payments',
        them: 'Settling up generally happens outside the app.',
        resyrv: 'Members pay statements in-app by card, ACH, or Cash App Pay; autopay included.',
        edge: true,
      },
      {
        dimension: 'Maintenance',
        them: 'Inspection reminders and squawk capture.',
        resyrv: 'The same, plus enforcement: a grounded aircraft removes itself from the schedule.',
        edge: true,
      },
      {
        dimension: 'Accounting',
        them: 'Export-oriented.',
        resyrv: 'Direct QuickBooks Online and Xero sync of invoices, payments, and members.',
        edge: true,
      },
      {
        dimension: 'Room to grow',
        them: 'Stays intentionally simple.',
        resyrv: 'The same workspace scales from two partners to a club with instructors and a rental line.',
        edge: true,
      },
    ],
    chooseThem: [
      'You are a sole owner who wants inspection reminders and tach tracking, nothing more.',
      'Your two-partner arrangement genuinely settles up fine over a text message.',
      'Minimal cost matters more than automation, and the treasurer work is not a burden yet.',
    ],
    faq: [
      {
        q: 'Is Resyrv overkill for a two-partner airplane?',
        a: 'Two partners are our smallest happy customers, because two partners still argue about money. If your split is genuinely trivial, start on the free trial and see whether the automated statement earns its keep in the first month - it usually does the first time one partner flies a lot more than the other.',
      },
      {
        q: 'Can we bring our history over?',
        a: 'Aircraft times, inspection baselines, member balances, and pilot logbooks (via ForeFlight-format import) all come across. Your operation does not start from zero.',
      },
      {
        q: 'What about maintenance reminders?',
        a: 'Fully covered - inspections, ADs, and component times with lead-time alerts. The difference is what happens when a deadline passes: Resyrv can pull the airplane off the schedule until it is signed off.',
      },
    ],
  },
  {
    slug: 'vs-schedule-master',
    name: 'Schedule Master',
    shortName: 'Schedule Master',
    title: 'Schedule Master Alternative for Clubs and Small Schools | Resyrv',
    metaDescription:
      'Comparing Schedule Master alternatives for a club or small school? Side-by-side on scheduling, billing and maintenance, what switching takes, and flat published pricing.',
    photo: '/marketing/taxi-blue-hour.webp',
    photoWidth: 1800,
    photoHeight: 1200,
    h1: 'It scheduled the last twenty years.',
    h1Accent: 'It should not schedule the next twenty.',
    heroCopy:
      'Schedule Master is a fixture of club aviation, and plenty of boards trust it because it has simply always worked. Resyrv respects that - and argues the bar has moved: members expect a modern app, treasurers expect automation, and the schedule should know whether the airplane is airworthy.',
    intro: [
      'Longevity deserves respect. Schedule Master has been taking club reservations since before smartphones, and a system that has run that long has earned its reliability reputation. If a club needs online scheduling and nothing else, it still delivers that.',
      'What has changed is everything around the schedule. Members now expect to book, see their balance, and pay from their phone. Treasurers expect flights to turn into charges without a spreadsheet. Safety officers expect an out-of-annual airplane to be unbookable. Those expectations are the gap between a scheduler and an operations platform - and closing that gap is exactly what Resyrv is.',
    ],
    rows: [
      {
        dimension: 'Scheduling',
        them: 'Proven online reservations with club rules - the core it is known for.',
        resyrv: 'Modern equivalent: conflict detection, per-member limits, cancellation policies, instructor availability.',
        edge: false,
      },
      {
        dimension: 'Member experience',
        them: 'Functional interface from an earlier era of the web.',
        resyrv: 'A modern app members actually enjoy: schedule, balance, documents, and payments in one place.',
        edge: true,
      },
      {
        dimension: 'Billing',
        them: 'Billing capabilities exist as an adjunct to scheduling.',
        resyrv: 'A real ledger at the center: metered charges, split rules, reserves, statements, FIFO allocation.',
        edge: true,
      },
      {
        dimension: 'Payments',
        them: 'Typically handled outside the system.',
        resyrv: 'Cards, ACH, and Cash App Pay in-app via Stripe, or your existing Authorize.net gateway.',
        edge: true,
      },
      {
        dimension: 'Maintenance',
        them: 'Scheduling-first; airworthiness lives elsewhere.',
        resyrv: 'Inspections, squawks, and ADs tracked - with grounding that removes the aircraft from the calendar.',
        edge: true,
      },
      {
        dimension: 'Documents & compliance',
        them: 'Outside the system.',
        resyrv: 'Insurance, airworthiness, and member documents with expiry tracking, in the same workspace.',
        edge: true,
      },
      {
        dimension: 'Accounting',
        them: 'Manual export and re-entry.',
        resyrv: 'QuickBooks Online and Xero sync built in.',
        edge: true,
      },
    ],
    chooseThem: [
      'Your club wants online scheduling only, at minimal cost, and is happy handling money and maintenance elsewhere.',
      'Your members are comfortable with the current interface and nobody is asking for more.',
      'Change itself is the biggest cost for your board right now.',
    ],
    faq: [
      {
        q: 'Our club has used it for fifteen years. Is switching painful?',
        a: 'The switch is mostly a data question, and the data is small: roster, balances, aircraft times, inspection dates. Clubs typically import those, run one overlap month, and cut over. The scheduling habits transfer in a day; the treasurer usually will not go back voluntarily.',
      },
      {
        q: 'Will older members cope with a new system?',
        a: 'A member\'s day-to-day is a calendar, a book button, and their balance. In practice the modern interface is easier for less technical members, not harder - and the board can see everything without phone calls.',
      },
      {
        q: 'What does it cost by comparison?',
        a: 'Resyrv\'s plans are flat and published on the pricing page. We will not characterize another vendor\'s pricing - put your club\'s current total cost (including the treasurer\'s spreadsheet hours) next to a plan and judge.',
      },
    ],
  },
  {
    slug: 'vs-myfbo',
    name: 'MyFBO.com',
    shortName: 'MyFBO',
    title: 'MyFBO Alternative: Resyrv vs MyFBO.com',
    metaDescription:
      'Looking for a MyFBO alternative? Resyrv vs MyFBO.com on scheduling, member experience, billing, maintenance, and setup, plus when MyFBO is still the right call.',
    photo: '/marketing/panel-vintage.webp',
    photoWidth: 1800,
    photoHeight: 1200,
    h1: 'It reaches into the fuel farm.',
    h1Accent: 'Most operations never needed it to.',
    heroCopy:
      'MyFBO.com is the most modular product in the category, and for an FBO that sells fuel, runs a ramp, and turns wrenches, that reach is real. Resyrv is for the flight school, club, or rental operation that was never going to touch most of those modules and wants the part it does use to feel like software built this decade.',
    intro: [
      'MyFBO earned its place by being the one system that could run an entire airport business: scheduling, dispatch and check-in, curriculum tracking, and then on into fuel sales, ramp sales, shop orders, and inventory. Its scheduling engine is well regarded, with conflict detection and drag-and-drop planning, and operators praise how quickly instructor availability can be set up. If your operation includes a fuel truck, this is one of the few products that knows what to do with it.',
      'The trade is the interface and the setup. Users describe it as antiquated and not mobile-friendly, and the module list is long enough that a school or club without an FBO spends its first weeks deciding what to switch off. Resyrv makes the opposite bet: one subscription, one workspace, a modern app on the ramp, and the whole thing configured in an afternoon. What it deliberately leaves out is the fuel farm.',
    ],
    rows: [
      {
        dimension: 'Built for',
        them: 'Full FBO operations: scheduling plus fuel, ramp, shop, and inventory.',
        resyrv: 'Flight schools, flying clubs, rental fleets, and partnerships running the flight line and the money.',
        edge: false,
      },
      {
        dimension: 'Scheduling',
        them: 'A capable engine with conflict detection and drag-and-drop planning.',
        resyrv: 'Conflict rejection on the server, per-tier booking rules, instructor availability windows, cancellation watch lists.',
        edge: false,
      },
      {
        dimension: 'Member experience',
        them: 'Users describe the interface as dated and not mobile-friendly.',
        resyrv: 'A modern web app plus a native iOS app: book, see your balance, report a squawk from the ramp.',
        edge: true,
      },
      {
        dimension: 'Setup',
        them: 'A long module list to evaluate, buy, and configure.',
        resyrv: 'One subscription with everything on. A setup wizard covers the organization, fleet, rates, and people.',
        edge: true,
      },
      {
        dimension: 'Billing',
        them: 'Billing capability spread across the module set.',
        resyrv: 'A real ledger at the center: itemized flight charges, split rules, reserves, statements, FIFO payment allocation.',
        edge: true,
      },
      {
        dimension: 'Maintenance',
        them: 'Tracked alongside the other modules.',
        resyrv: 'Inspections, squawks, and ADs with grounding that removes the aircraft from the calendar and blocks dispatch.',
        edge: true,
      },
      {
        dimension: 'Fuel and ramp',
        them: 'Fuel sales, ramp sales, shop orders, and inventory are in the product.',
        resyrv: 'No fuel or ramp sales. Resyrv bills wet or dry against a house fuel price, and keeps work orders and a parts catalog for your own fleet.',
        edge: false,
      },
      {
        dimension: 'Accounting',
        them: 'Depends on which modules you run.',
        resyrv: 'QuickBooks Online and Xero sync built in.',
        edge: true,
      },
    ],
    chooseThem: [
      'You sell fuel, run a ramp, or run a stockroom as its own business, and want one system to cover all of it.',
      'Your staff already know MyFBO and the operation runs on modules Resyrv does not have.',
      'A dated interface is a cosmetic complaint for your members, not a reason people avoid the system.',
    ],
    faq: [
      {
        q: 'We sell fuel. Can Resyrv replace MyFBO for us?',
        a: 'Not for the fuel side. Resyrv runs scheduling, dispatch, billing, maintenance, documents, and payments for the flying operation, and it bills wet or dry against the house fuel price you set. It does track work orders and a parts catalog for your own fleet, but it does not sell fuel, run a ramp, or invoice outside maintenance customers. Operators in that position keep a fuel and ramp system and run the school, club, or rental side on Resyrv.',
      },
      {
        q: 'Is switching a big project?',
        a: 'The data is small: roster, balances, aircraft times, inspection dates. Members import from a spreadsheet or join through a link, opening balances take whoever runs the books one afternoon, and historical flight records can be archived rather than migrated. Most operations are booking from the new system inside a week.',
      },
      {
        q: 'Will our instructors lose the availability setup they like?',
        a: 'No. Each instructor publishes recurring bookable windows by day and hour plus dated time off, and students only see slots where the instructor and the aircraft are both free. A dual flight books both as one reservation.',
      },
    ],
  },
  {
    slug: 'vs-aircraft-clubs',
    name: 'Aircraft Clubs',
    shortName: 'AircraftClubs',
    title: 'Aircraft Clubs Alternative: Resyrv vs AircraftClubs.com',
    metaDescription:
      'Looking for an AircraftClubs.com alternative? Resyrv vs Aircraft Clubs on scheduling rules, instructor booking, maintenance grounding, billing, and notifications.',
    photo: '/marketing/cockpit-clouds.webp',
    photoWidth: 1050,
    photoHeight: 1400,
    h1: 'A scheduler clubs have trusted for years.',
    h1Accent: 'The rest of the club still runs on email.',
    heroCopy:
      'Aircraft Clubs has scheduled club aircraft reliably for a long time, including clubs well past sixty members. Resyrv respects that and picks up where a scheduler stops: instructor booking and maintenance enforcement included rather than added on, money in the same workspace, and notifications members do not have to filter.',
    intro: [
      'AircraftClubs.com is a name that comes up unprompted in club forums, and for good reason: it works. Clubs cite its granular scheduling rules, decent parity between the phone and the desktop, and the fact that it keeps running at sizes where cheaper tools fall over. For a club that wants online reservations with rules the board can lock down, it has delivered that for years.',
      'The complaints clubs raise are about everything around the calendar. Instructor booking and maintenance tracking are offered as add-ons rather than as part of the product. Members describe too many email notifications. Admins have found features appear without warning and had to switch them off. None of that is a scheduling failure. It is what a club needs once scheduling itself is solved.',
    ],
    rows: [
      {
        dimension: 'Scheduling',
        them: 'Granular rule locking, proven in large clubs.',
        resyrv: 'Modern equivalent: server-side conflict rejection, per-tier limits, cancellation policies, instructor availability.',
        edge: false,
      },
      {
        dimension: 'Instructor booking',
        them: 'Offered as a separate add-on.',
        resyrv: 'Included. A dual flight books the aircraft and the CFI as one reservation, checked against the instructor\'s published windows.',
        edge: true,
      },
      {
        dimension: 'Maintenance',
        them: 'Tracking available as an add-on.',
        resyrv: 'Inspections, squawks, and ADs included, with grounding that removes the aircraft from the calendar and blocks dispatch.',
        edge: true,
      },
      {
        dimension: 'Notifications',
        them: 'Members report too much email.',
        resyrv: 'Cancellation alerts filtered to the aircraft, days, and times each member asked for. Reminders fire on the lead days you set.',
        edge: true,
      },
      {
        dimension: 'Billing and payments',
        them: 'Scheduling is the center; most clubs settle money elsewhere.',
        resyrv: 'Flights meter into itemized charges, dues post themselves, members pay by card, ACH, or Cash App Pay in-app, autopay included.',
        edge: true,
      },
      {
        dimension: 'Reserves',
        them: 'Outside the system.',
        resyrv: 'Engine, prop, and avionics reserves accrue per hour flown with balances visible to every member.',
        edge: true,
      },
      {
        dimension: 'Member experience',
        them: 'Reasonable parity between phone and desktop.',
        resyrv: 'A modern web app plus a native iOS app: schedule, balance, documents, and payments in one place.',
        edge: true,
      },
      {
        dimension: 'Accounting',
        them: 'Export and re-enter.',
        resyrv: 'QuickBooks Online and Xero sync built in.',
        edge: true,
      },
    ],
    chooseThem: [
      'Your club wants online scheduling with lockable rules and nothing else, at minimal cost.',
      'The treasurer is happy with the current billing setup and nobody is asking for statements they can read on a phone.',
      'You run a large club with rules already dialed in, and change is the biggest cost on the board\'s agenda right now.',
    ],
    faq: [
      {
        q: 'Will members drown in email again?',
        a: 'No. A member subscribes to the aircraft they want and tells Resyrv which days and times they are free. Only a cancellation that matches gets an email. Everything else, reminders included, fires on the schedule the club chooses.',
      },
      {
        q: 'Do we pay extra to schedule instructors or track maintenance?',
        a: 'No. Instructor availability, dual bookings, inspections, squawks, ADs, and grounding are part of every plan. Plans are flat per organization and published on the pricing page.',
      },
      {
        q: 'Can we move a large club without a fight?',
        a: 'The scheduling habits transfer in a day because the calendar works the way members expect. The heavy part is agreeing on rate rules and opening balances, which is a treasurer afternoon. Clubs typically run one overlap month and cut over.',
      },
    ],
  },
  {
    slug: 'vs-aviatize',
    name: 'Aviatize',
    shortName: 'Aviatize',
    title: 'Aviatize Alternative: Resyrv vs Aviatize',
    metaDescription:
      'Looking for an Aviatize alternative? Resyrv vs Aviatize on setup, billing, cost sharing, live aircraft tracking, and mobile apps, plus when Aviatize fits better.',
    photo: '/marketing/trainer-sunset-landing.webp',
    photoWidth: 1800,
    photoHeight: 1200,
    h1: 'Built for every aviation authority on earth.',
    h1Accent: 'Resyrv is built for the one you fly under.',
    heroCopy:
      'Aviatize is a modern all-in-one with apps on both phones and support for training structures on every continent. Resyrv is narrower on purpose: US clubs, schools, rental fleets, and partnerships, with the money side and the cost-sharing side built as deeply as the schedule, and a setup a small operation finishes in an afternoon.',
    intro: [
      'Aviatize deserves credit for a lot. The interface is current, the apps run on iOS and Android, maintenance validation blocks bookings on an unairworthy aircraft, and it handles FAA Part 61 and 141 alongside European ATO and DTO structures. For a training organization outside the United States, or one operating under more than one authority, it is one of the few products that takes that seriously.',
      'Aviatize says itself that it carries more configuration than a small Part 61 school needs, and that real-time aircraft tracking is an integration rather than something built in. Resyrv starts from the other end: a two-airplane school or a fifteen-member club should be booking within the hour, cost sharing and reserves should be first-class rather than an invoicing feature, and the live flight board should be on the wall without a third product.',
    ],
    rows: [
      {
        dimension: 'Built for',
        them: 'Training organizations worldwide, across many aviation authorities.',
        resyrv: 'US flight schools, flying clubs, rental fleets, and partnerships.',
        edge: false,
      },
      {
        dimension: 'Scheduling',
        them: 'Modern scheduling with maintenance validation on bookings.',
        resyrv: 'The same, plus per-tier booking rules, cancellation watch lists, and instructor availability windows.',
        edge: false,
      },
      {
        dimension: 'Setup',
        them: 'More configuration than a small Part 61 school needs, by its own account.',
        resyrv: 'A setup wizard for the organization, fleet, rates, and people. Small operations are booking the same day.',
        edge: true,
      },
      {
        dimension: 'Billing',
        them: 'Invoicing from Hobbs time.',
        resyrv: 'A full ledger: itemized charges, split rules for partnerships, reserves that accrue per hour, statements, autopay, FIFO allocation.',
        edge: true,
      },
      {
        dimension: 'Clubs and partnerships',
        them: 'School-first.',
        resyrv: 'Cost sharing, reserve funds, member tiers, and dues are built in for clubs and co-owners, not adapted from a school product.',
        edge: true,
      },
      {
        dimension: 'Live aircraft tracking',
        them: 'Not built in; available through integration.',
        resyrv: 'A live flights board with a map, built in, ready for a kiosk screen at the airport.',
        edge: true,
      },
      {
        dimension: 'Mobile apps',
        them: 'Native apps on iOS and Android.',
        resyrv: 'A native iOS app and a mobile web app that works on any phone.',
        edge: false,
      },
      {
        dimension: 'Accounting',
        them: 'Invoicing is in the product.',
        resyrv: 'QuickBooks Online and Xero sync of invoices, payments, and members, with profit-and-loss reporting in the app.',
        edge: true,
      },
    ],
    chooseThem: [
      'You operate outside the United States or under more than one aviation authority.',
      'Your members need a native Android app rather than a mobile web app.',
      'You are a training organization first and cost sharing, reserves, and club governance are not part of the job.',
    ],
    faq: [
      {
        q: 'Does Resyrv support Part 141 the way Aviatize does?',
        a: 'Yes. Resyrv ships Part 61 and Part 141 syllabus templates with stage checks and per-lesson grading, and training records are tied to the flight and the invoice in the same workspace. What it does not do is European ATO or DTO structures.',
      },
      {
        q: 'Is Resyrv really faster to set up?',
        a: 'For a small operation, yes. The wizard walks through the organization, the first aircraft with its rate, how you operate, and your people. A club or school that already knows its rates can be booking the same afternoon. Larger operations add fleet, maintenance baselines, and opening balances over a few days.',
      },
      {
        q: 'Do you have an Android app?',
        a: 'Not a native one. Resyrv ships a native iOS app, and the web app is built for phones so Android members book, check their balance, and report squawks from the browser. If a native Android app is a requirement, Aviatize is the better fit today.',
      },
    ],
  },
]

export function getCompetitor(slug) {
  return COMPETITORS.find((c) => c.slug === slug) || null
}

export function comparePath(slug) {
  return `/compare/${slug}`
}
